import React from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import { useRouter } from 'next/router';
import FrontPageStyles from './StoriesFrontPage.styled';
import { getSiteUrl } from '@utils/config';
import { getFullPageControlContentV1 } from '@services/contentfulService';
import Hero from '@components/Stories/Hero/Hero';
import Overview from '@components/Stories/Overview/Overview';
import FeatureCard from '@components/Stories/FeatureCard/FeatureCard';
import FullWidthFeatureCard from '@components/Stories/FullWidthFeatureCard/FullWidthFeatureCard';
import ImageGrid from '@components/Stories/ImageGrid/ImageGrid';
import CtaSection from '@components/Stories/CtaSection/CtaSection';
import PreFooterCTA from '@components/Stories/PreFooterCTA/PreFooterCTA';
import FeaturedHomes from '@components/Home/FeaturedHomes/FeaturedHomes';
import Disclaimer from '@components/Stories/Disclaimer/Disclaimer';
import ThreeImageGrid from '@components/Stories/ThreeImageGrid/ThreeImageGrid';

const FrontPage = ({ pageInfo }) => {
  const router = useRouter();

  const getOgImage = () => {
    return (
      pageInfo?.ogImage?.file?.url ||
      getSiteUrl('/public-assets/images/clayton-logo.jpg')
    );
  };

  const gridNameThreeImages = 'Three Images: Large-Small';

  const FeatureContent = ({ textSections, imageGrids }) => {
    const insertGtmInformation = (sections) => {
      //GTM information for Left/Right sections.  Include as many as needed.
      const gtmFeatureCard = [
        {
          text: 'gtm-ugc-testimonial-1-text',
          image: 'gtm-ugc-testimonial-1-image',
          header: 'gtm-ugc-testimonial-1-header',
        },
        {
          text: 'gtm-ugc-testimonial-2-text',
          image: 'gtm-ugc-testimonial-2-image',
          header: 'gtm-ugc-testimonial-2-header',
        },
        {
          text: 'gtm-ugc-testimonial-3-text',
          image: 'gtm-ugc-testimonial-3-image',
          header: 'gtm-ugc-testimonial-3-header',
        },
      ];

      //GTM information for Background sections.  Include as many as needed.
      // See comment below for an example
      // const gtmFullWidthFeatureCard = [
      //   { trigger: 'ga-csr-bg-team-members-boundless' },
      // ];
      const gtmFullWidthFeatureCard = [];

      /*
      Each section is defined as Left Side, Right Side, or Background.
      Inspect each section.  Pop the gtm information from the related array, and insert in the section.
      */
      return sections.map((section) => {
        if (
          section.imageLocation === 'Left Side' ||
          section.imageLocation === 'Right Side'
        ) {
          return {
            ...section,
            gtm: gtmFeatureCard ? gtmFeatureCard.shift() : {},
          };
        }
        if (section.imageLocation === 'Background') {
          return {
            ...section,
            gtm: gtmFullWidthFeatureCard ? gtmFullWidthFeatureCard.shift() : {},
          };
        }
      });
    };

    //Inspect each text section, and include the related GTM information.
    const cardSections = textSections ? insertGtmInformation(textSections) : [];

    /*
    The text sections are divided in two groups.  (The order of text sections is defined by the API/Contentful.)
    An image grid is placed after each group.
    */
    const firstGroup = !cardSections ? [] : cardSections.slice(0, 4);
    const secondGroup = !cardSections ? [] : cardSections.slice(4, 8);
    const firstImageGrid = !imageGrids
      ? []
      : imageGrids.slice(0, 1).map((g) => ({
          ...g,
          imageLocation: 'Grid',
          isFirstImageGrid: true,
        }));
    const secondImageGrid = !imageGrids
      ? []
      : imageGrids.slice(1, 2).map((g) => ({
          ...g,
          imageLocation: 'Grid',
          isFirstImageGrid: false,
        }));

    let cards = [];
    if (firstGroup) {
      cards.push(...firstGroup);
    }
    if (firstImageGrid) {
      cards.push(...firstImageGrid);
    }
    if (secondGroup) {
      cards.push(...secondGroup);
    }
    if (secondImageGrid) {
      cards.push(...secondImageGrid);
    }

    //Remember the anchor-link references.
    let anchors = [];

    //Draw each card.
    const cardGrids = cards?.map((card, index) => {
      //anchors are only recorded for Left/Right card styles.
      if (
        card.imageLocation === 'Left Side' ||
        card.imageLocation === 'Right Side'
      ) {
        anchors.push({
          title: card.sectionHeader,
          anchorRef: index,
          gtm: card.gtm?.trigger
            ? { trigger: card.gtm.trigger + '-anchor' }
            : {},
        });
      }
      return <FeatureWrapper key={index} card={card} cardFeatureId={index} />;
    });

    return cardGrids;
  };

  const FeatureWrapper = ({ card, cardFeatureId }) => {
    switch (card.imageLocation) {
      case 'Left Side':
        return (
          <FeatureCard
            card={card}
            cardFeatureId={cardFeatureId}
            imagePlacement="left"
          />
        );
      case 'Right Side':
        return (
          <FeatureCard
            card={card}
            cardFeatureId={cardFeatureId}
            imagePlacement="right"
          />
        );
      case 'Background':
        return <FullWidthFeatureCard card={card} />;
      case 'Grid':
        return (
          <ImageGrid
            cards={card.images}
            cardFeatureId={cardFeatureId}
            isFirstImageGrid={card.isFirstImageGrid}
          />
        );
      default:
        return <p>Unknown Content Type - ${card.contentfulName}</p>;
    }
  };

  FeatureWrapper.propTypes = {
    card: PropTypes.object,
    cardFeatureId: PropTypes.number,
  };

  return (
    <FrontPageStyles>
      <Head>
        <title>{pageInfo?.titleTag}</title>
        <meta name="description" content={pageInfo?.metaDescription} />
        <link rel="canonical" href={`${getSiteUrl(router.pathname)}`} />
        <meta property="og:image" content={getOgImage()} />
      </Head>
      {pageInfo?.heroImage && (
        <Hero
          heroImage={pageInfo.heroImage}
          mobileHeroImage={pageInfo.mobileHeroImage}
          heroText={pageInfo.heroText}
          pageTitle={pageInfo.pageTitle}
        />
      )}
      {(pageInfo?.overviewHeader ||
        pageInfo?.overview ||
        pageInfo?.quotes?.length > 0) && (
        <Overview
          overviewHeader={pageInfo.overviewHeader}
          overview={pageInfo.overview}
          quote={pageInfo.quotes?.[0]}
          overviewLinkText={pageInfo.overviewLinkText}
          overviewLink={pageInfo.overviewLink}
        />
      )}
      <FeatureContent
        textSections={pageInfo?.mainContentSectionTypes?.textSections}
        imageGrids={
          pageInfo?.mainContentSectionTypes?.imageGrids?.[0]?.gridLayout !==
            gridNameThreeImages && pageInfo?.mainContentSectionTypes?.imageGrids
        }
        headline={pageInfo?.mainContentHeader}
      />
      {pageInfo?.mainContentSectionTypes?.featuredHomesSections?.[0] && (
        <FeaturedHomes
          section={pageInfo?.mainContentSectionTypes?.featuredHomesSections[0]}
          headlineLinkUrl={
            pageInfo?.mainContentSectionTypes?.featuredHomesSections[0]
              .sectionLink
          }
          headlineLinkClassName={'gtm-ugc-tiktok-header'}
          isHomePage={false}
          includeModelsWithoutDealers={true}
        />
      )}
      {pageInfo?.mainContentSectionTypes?.ctaSections?.[0] && (
        <CtaSection cta={pageInfo?.mainContentSectionTypes?.ctaSections?.[0]} />
      )}

      {pageInfo?.mainContentSectionTypes?.imageGrids?.[0]?.gridLayout ===
        gridNameThreeImages && (
        <ThreeImageGrid
          images={pageInfo?.mainContentSectionTypes?.imageGrids[0]?.images}
          gtmDictionary={[
            'gtm-ugc-grid-image-1',
            'gtm-ugc-grid-image-2',
            'gtm-ugc-grid-image-3',
          ]}
        />
      )}
      {pageInfo?.preFooterCTA && (
        <PreFooterCTA
          alignCtaContent={pageInfo?.preFooterCTA?.alignCtaContent}
          buttonAndTextColors={pageInfo?.preFooterCTA?.buttonAndTextColors}
          ctaBackgroundColor={pageInfo?.preFooterCTA?.ctaBackgroundColor}
          ctaHeader={pageInfo?.preFooterCTA?.ctaHeader}
          ctaImage={pageInfo?.preFooterCTA?.ctaImage?.file?.url}
          firstCtaButtonText={pageInfo?.preFooterCTA?.firstCtaButtonText}
          firstCtaButtonUrl={pageInfo?.preFooterCTA?.firstCtaButtonUrl}
        />
      )}
      {pageInfo?.disclaimer && (
        <Disclaimer disclaimerContent={pageInfo.disclaimer} />
      )}
    </FrontPageStyles>
  );
};

export default FrontPage;

const fccPageKey = '2nGPb59LQ9ZLhYUnMrR5tn';

export async function getServerSideProps() {
  let fccPageContent;
  await Promise.all([
    getFullPageControlContentV1(fccPageKey)
      .then((response) => {
        fccPageContent = response;
      })
      .catch(() => {
        console.error('no content');
      }),
  ]);

  return {
    props: {
      pageInfo: fccPageContent,
    },
  };
}

FrontPage.propTypes = {
  pageInfo: PropTypes.object,
};
